import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import LeadCTA from "@/components/LeadCTA";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { PAGES, PAGE_IMAGES, pathFor, NAV_GROUPS } from "@/data/pages";
import { useEffect } from "react";

/**
 * Insights / Blog listing page — PwC-style.
 * Surfaces every page in the `insights` and `blog` groups as an editorial card.
 */
export default function InsightsListPage() {
  const { lang, t } = useLang();
  const all = lang === "zh" ? ZH : EN;

  // Use the registry's order for both insights and blog groups
  const allIds = [...(NAV_GROUPS.blog || []), ...(NAV_GROUPS.insights || [])];
  const items = allIds.map((id) => ({ id, page: PAGES.find((p) => p.id === id), c: all[id] })).filter((x) => x.page && x.c);

  // First entry is featured (large), the rest go in the grid
  const [featured, ...rest] = items;

  useEffect(() => {
    document.documentElement.classList.toggle("lang-zh", lang === "zh");
    return () => document.documentElement.classList.remove("lang-zh");
  }, [lang]);

  const pageTitle = lang === "zh" ? "深度洞察 — ABS Solutions" : "Insights — ABS Solutions";
  const pageDescription = lang === "zh"
    ? "ABS Solutions 团队发布的实用税务、会计、SMSF 与商业策略指南——为澳大利亚个人、企业及华人社群提供可落地的专业建议。"
    : "Sharp, practical insights from the ABS Solutions team on tax, accounting, SMSF and business strategy — written for Australian individuals, businesses and the Chinese-Australian community.";

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO
        pageId="insights"
        title={pageTitle}
        description={pageDescription}
        image="/images/blog/cash-flow-management.jpg"
      />

      {/* Hero */}
      <section className="bg-[var(--abs-cream)] pt-32 md:pt-40 pb-16 md:pb-20 border-b border-[var(--abs-line)]" data-testid="insights-hero">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-6">
              {lang === "zh" ? "深度洞察" : "Insights"}
            </div>
            <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] max-w-4xl tracking-tight" data-testid="insights-h1">
              {lang === "zh"
                ? "前沿洞察，深度解析。"
                : "Sharp takes on what's next."}
            </h1>
            <p className="mt-8 text-[17px] leading-relaxed text-[var(--abs-mute)] max-w-2xl">
              {lang === "zh"
                ? "由 ABS Solutions 团队撰写的实用税务、会计、SMSF 与商业策略指南，为澳大利亚个人、企业与华人社群提供可落地的专业建议。"
                : "Sharp, practical insights from the ABS Solutions team on tax, accounting, SMSF and business strategy. Written for Australian individuals, businesses and the Chinese-Australian community."}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Featured (first article) */}
      {featured && (
        <section className="bg-white py-16 md:py-24" data-testid="insights-featured">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
            <ScrollReveal>
              <Link
                to={pathFor(featured.id, lang)}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 group"
                data-testid={`insights-featured-${featured.id}`}
              >
                <div className="lg:col-span-7">
                  <div className="relative overflow-hidden img-frame">
                    <img
                      src={PAGE_IMAGES[featured.id]}
                      alt={featured.c.h1 || featured.c.title}
                      className="w-full aspect-[16/10] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="lg:col-span-5 lg:pt-10 flex flex-col">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-4">
                    {lang === "zh" ? "精选文章" : "Featured article"}
                  </div>
                  <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-[1.06] tracking-tight">
                    {featured.c.h1 || featured.c.title}
                  </h2>
                  {featured.c.intro && (
                    <p className="mt-6 text-[16px] leading-relaxed text-[var(--abs-mute)] max-w-md">
                      {featured.c.intro}
                    </p>
                  )}
                  <div className="mt-8 flex items-center gap-2 text-[15px] group-hover:text-[var(--abs-orange)] transition-colors">
                    {lang === "zh" ? "阅读全文" : "Read article"}
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Grid (remaining articles) */}
      {rest.length > 0 && (
        <section className="bg-[var(--abs-cream)] py-20 md:py-24 border-t border-[var(--abs-line)]" data-testid="insights-grid">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
            <ScrollReveal>
              <h2 className="serif text-3xl md:text-4xl leading-[1.06] tracking-tight max-w-2xl mb-12">
                {lang === "zh" ? "更多专业洞察" : "More insights from our team"}
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {rest.map((it, idx) => (
                <ScrollReveal key={it.id} delay={idx * 60}>
                  <Link
                    to={pathFor(it.id, lang)}
                    className="block group"
                    data-testid={`insights-card-${it.id}`}
                  >
                    <div className="relative overflow-hidden img-frame mb-6">
                      <img
                        src={PAGE_IMAGES[it.id]}
                        alt={it.c.h1 || it.c.title}
                        className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-3">
                      {it.page.group === "blog"
                        ? (lang === "zh" ? "深度洞察" : "Article")
                        : t.groupLabels.insights}
                    </div>
                    <h3 className="serif text-xl md:text-[22px] leading-[1.15] tracking-tight group-hover:text-[var(--abs-orange)] transition-colors">
                      {it.c.h1 || it.c.title}
                    </h3>
                    {it.c.intro && (
                      <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--abs-mute)] line-clamp-3">
                        {it.c.intro}
                      </p>
                    )}
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <LeadCTA />
    </div>
  );
}
